import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Box, Typography, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppContext, SttPage } from '../../../sporttia/all';
import { getPath } from '../../Pages';
import { useLoader, usePageHeader } from '../../../lib/hooks';
import useReportsService from '../../../services/ReportsService';
import { canSeeActivityReports } from './canSeeActivityReports';

export default function SCReports() {
    usePageHeader('Reports');
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();

    const reportService = useReportsService();
    const reportsQuery = reportService.useGetReports({ scope: 'SC' });
    const [, loader] = useLoader([reportsQuery.status]);

    const reportGroups: {
        [key: string]: Array<{
            caption: string;
            params: { id: number };
            path: 'report';
        }>;
    } = {};

    reportsQuery?.data?.rows.forEach((reportItem: any) => {
        if (reportItem.group && !(reportItem.group in reportGroups)) {
            // Inicializamos el grupo
            reportGroups[reportItem.group] = [];
        }
        if (reportGroups[reportItem.group]) {
            reportGroups[reportItem.group]!.push({
                caption: reportItem.name,
                params: { id: reportItem?.id },
                path: 'report',
            });
        }
    });

    const reportList: Array<{
        caption: string;
        params: { id: number };
        path: 'report';
    }> = Object.values(reportGroups).flat();

    /* TODO eliminar este código comentado cuando se aclare donde se va a filtrar por privilegios
       en la nueva forma de leer los reportes

    const reportGroups = [
        {
            title: t('Bookings'),
            reports: [
                {
                    caption: 'OccupationOfFacilities',
                    params: { id: 8 },
                },
                {
                    caption: 'Seating',
                    params: { id: 53 },
                },
            ],
        },
    ]; */

    if (!canSeeActivityReports(cxt)) {
        delete reportGroups.ACTIVITY;
    }

    const goReport = (reportPicked: { params: { id: number } }) => {
        navigate(getPath('report' as any, reportPicked.params), {
            state: {
                reportGroups: reportList,
            },
        });
    };

    return (
        <SttPage title={t('Reports')}>
            <Box p={2}>
                {loader}
                <Grid container spacing={3}>
                    {reportGroups &&
                        Object.keys(reportGroups).map((keyGroup: any) => (
                            <Grid
                                size={{ md: 4, sm: 6, xs: 12 }}
                                key={keyGroup}
                            >
                                <Typography variant="h5">
                                    {t(
                                        keyGroup.length > 0
                                            ? keyGroup.charAt(0) +
                                                  keyGroup
                                                      .slice(1)
                                                      .toLowerCase()
                                            : '',
                                    )}
                                </Typography>

                                {reportGroups[keyGroup] &&
                                    reportGroups[keyGroup]?.map(
                                        (reportItem: any) => (
                                            <Box
                                                my={1}
                                                key={reportItem.params.id}
                                            >
                                                <Link
                                                    sx={{
                                                        color: 'var(--brand-text)',
                                                        textDecoration: 'none',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            textDecoration:
                                                                'underline',
                                                        },
                                                    }}
                                                    onClick={() =>
                                                        goReport({
                                                            params: {
                                                                id: reportItem
                                                                    .params.id,
                                                            },
                                                        })
                                                    }
                                                >
                                                    {t(
                                                        `reports.report_${reportItem.params.id}`,
                                                        {
                                                            defaultValue:
                                                                reportItem.caption,
                                                        },
                                                    )}
                                                </Link>
                                            </Box>
                                        ),
                                    )}
                            </Grid>
                        ))}
                    <Grid size={{ md: 4, sm: 6, xs: 12 }}>
                        <Typography variant="h5">
                            {t('reports.stats')}
                        </Typography>

                        <Box my={1}>
                            <Link
                                sx={{
                                    color: 'var(--brand-text)',
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                                onClick={() => {
                                    navigate(getPath('scStats' as any));
                                }}
                            >
                                {t('reports.seeStats')}
                            </Link>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </SttPage>
    );
}
